import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import InstanceList from '../Instance/InstanceList';
import QueryInterface from '../Query/QueryInterface';
import SchemaViewer from '../Schema/SchemaViewer';
import CustomAttributesManager from '../CustomAttributes/CustomAttributesManager';
import ESIndexManager from '../ESIndex/ESIndexManager';
import CustomIndexManager from '../DBIndex/CustomIndexManager';
import MemoryManager from '../DBIndex/MemoryManager';
import SummaryViewer from '../Summary/SummaryViewer';
import '../shared.css';
import './VersionDetails.css';

type TabType = 'instances' | 'query' | 'schema' | 'summary' | 'attributes' | 'es-indexes' | 'custom-indexes' | 'memory' | 'files';

const VersionDetails: React.FC = () => {
  const { kgId, versionId } = useParams<{ kgId: string; versionId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('instances');
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadVersionInfo = useCallback(async () => {
    if (!kgId || !versionId) return;
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.getVersion(kgId, versionId);
      setVersionInfo(response.data);
      // Persist version badge info for the topbar
      sessionStorage.setItem('versionBadge', JSON.stringify({
        versionId,
        published: response.data?.attributes?.published ?? false,
      }));
      window.dispatchEvent(new Event('versionBadgeUpdated'));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load version details'));
    } finally {
      setLoading(false);
    }
  }, [kgId, versionId]);

  useEffect(() => {
    if (kgId && versionId) loadVersionInfo();
  }, [kgId, versionId, loadVersionInfo]);

  if (loading) {
    return (
      <div className="version-details-container">
        <div className="page-loader"><div className="page-spinner" /></div>
      </div>
    );
  }

  const kgName = versionInfo?.attributes?.kg_name || kgId;
  const published: boolean = versionInfo?.attributes?.published ?? false;
  const gdsEnabled: boolean = versionInfo?.attributes?.gds_enabled ?? false;
  const dataVersion = versionInfo?.attributes?.data_version;
  const dataFiles = versionInfo?.attributes?.data_files;
  const nodeFiles: string[] = dataFiles?.nodes ?? [];
  const edgeFiles: string[] = dataFiles?.edges ?? [];
  const hasFiles = nodeFiles.length > 0 || edgeFiles.length > 0;

  return (
    <div className="version-details-container">

      {error && <div className="error-message">{error}</div>}

      <div className="tabs" role="tablist">
        <button
          role="tab"
          aria-selected={activeTab === 'instances'}
          className={`tab ${activeTab === 'instances' ? 'active' : ''}`}
          onClick={() => setActiveTab('instances')}
        >
          Instances
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'query'}
          className={`tab ${activeTab === 'query' ? 'active' : ''}`}
          onClick={() => setActiveTab('query')}
        >
          Query
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'schema'}
          className={`tab ${activeTab === 'schema' ? 'active' : ''}`}
          onClick={() => setActiveTab('schema')}
        >
          Schema
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'summary'}
          className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => setActiveTab('summary')}
        >
          Summary
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'attributes'}
          className={`tab ${activeTab === 'attributes' ? 'active' : ''}`}
          onClick={() => setActiveTab('attributes')}
        >
          Custom Attributes
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'es-indexes'}
          className={`tab ${activeTab === 'es-indexes' ? 'active' : ''}`}
          onClick={() => setActiveTab('es-indexes')}
        >
          ES Indexes
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'custom-indexes'}
          className={`tab ${activeTab === 'custom-indexes' ? 'active' : ''}`}
          onClick={() => setActiveTab('custom-indexes')}
        >
          Custom Indexes
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'memory'}
          className={`tab ${activeTab === 'memory' ? 'active' : ''}`}
          onClick={() => setActiveTab('memory')}
        >
          Memory
        </button>
        <button
          role="tab"
          aria-selected={activeTab === 'files'}
          className={`tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          Files
        </button>
      </div>

      <div className="tab-content">
        {kgId && versionId && (
          <>
            <div className={`vd-tab-panel${activeTab === 'instances' ? '' : ' tab-panel-hidden'}`}>
              <InstanceList kgId={kgId} versionId={versionId} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'query' ? '' : ' tab-panel-hidden'}`}>
              <QueryInterface kgId={kgId} versionId={versionId} kgName={kgName} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'schema' ? '' : ' tab-panel-hidden'}`}>
              <SchemaViewer kgId={kgId} versionId={versionId} published={published} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'summary' ? '' : ' tab-panel-hidden'}`}>
              <SummaryViewer kgId={kgId} versionId={versionId} published={published} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'attributes' ? '' : ' tab-panel-hidden'}`}>
              <CustomAttributesManager kgId={kgId} versionId={versionId} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'es-indexes' ? '' : ' tab-panel-hidden'}`}>
              <ESIndexManager kgId={kgId} versionId={versionId} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'custom-indexes' ? '' : ' tab-panel-hidden'}`}>
              <CustomIndexManager kgId={kgId} versionId={versionId} published={published} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'memory' ? '' : ' tab-panel-hidden'}`}>
              <MemoryManager kgId={kgId} versionId={versionId} gdsEnabled={gdsEnabled} />
            </div>
            <div className={`vd-tab-panel${activeTab === 'files' ? '' : ' tab-panel-hidden'}`}>
              <div className="tab-root">
                {!hasFiles ? (
                  <div className="tab-empty">
                    <div className="tab-empty-icon">📂</div>
                    <div className="tab-empty-title">No source files</div>
                    <div className="tab-empty-body">No node or edge files were recorded for this version.</div>
                  </div>
                ) : (
                  <div className="vd-files-grid">
                    <div className="tab-panel">
                      <div className="tab-panel-hd">
                        <span className="tab-panel-title">Node Files</span>
                        <span className="tab-stat-pill">{nodeFiles.length} file{nodeFiles.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="tab-table-wrap">
                        <table className="tab-table">
                          <tbody>
                            {nodeFiles.length === 0
                              ? <tr><td style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>No node files</td></tr>
                              : nodeFiles.map((f) => <tr key={f}><td style={{ wordBreak: 'break-all' }}>{f}</td></tr>)
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="tab-panel">
                      <div className="tab-panel-hd">
                        <span className="tab-panel-title">Edge Files</span>
                        <span className="tab-stat-pill">{edgeFiles.length} file{edgeFiles.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="tab-table-wrap">
                        <table className="tab-table">
                          <tbody>
                            {edgeFiles.length === 0
                              ? <tr><td style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>No edge files</td></tr>
                              : edgeFiles.map((f) => <tr key={f}><td style={{ wordBreak: 'break-all' }}>{f}</td></tr>)
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VersionDetails;
